import {Cog6ToothIcon} from '@heroicons/react/20/solid';
import {useCallback, useEffect, useState} from 'react';
import * as U from './util';
import {AppConfigModal} from './app-config-modal';
import {ChannelConfigModal} from './channel-config-modal';
import {AppConfiguration, ChannelConfiguration, PineridgeChannel} from './pineridge-channel';
import {useEventHandler, emitEvent, Event, UserEditChannelConfigEvent} from './events';

export function AppConfigPanel() {
  const [appConfig, setAppConfig] = useState(U.getAppConfigFromLocalStorage());
  const [appConfigModalOpen, setAppConfigModalOpen] = useState(false);
  const [isUserAgentSet, setIsUserAgentSet] = useState(false);

  const [configSource, setConfigSource] = useState<PineridgeChannel | ChannelConfiguration>(appConfig);
  const [isChannelConfigModalOpen, setIsChannelConfigModalOpen] = useState(false);

  useEventHandler(Event.PINERIDGE_DEFAULT_PAK_UPDATED, (pak: string) => {
    const updatedConfig = {
      ...appConfig,
      pak,
    };

    setAppConfig(updatedConfig);
  });

  useEventHandler(Event.PINERIDGE_ADD_CHANNEL_FROM_PAK, (channelConfig: ChannelConfiguration) => {
    setConfigSource(channelConfig);
    setIsChannelConfigModalOpen(true);
  });

  useEventHandler(Event.PINERIDGE_EDIT_CHANNEL, (channel: PineridgeChannel) => {
    setConfigSource(channel);
    setIsChannelConfigModalOpen(true);
  });

  const handleSetChannelConfig = useCallback(
    (channelConfig: ChannelConfiguration) => {
      if (configSource instanceof PineridgeChannel) {
        const eventData: UserEditChannelConfigEvent = {
          pineridgeChannel: configSource,
          channelConfig,
        };
        emitEvent(Event.USER_FINISH_EDIT_CHANNEL, eventData);
      } else {
        emitEvent(Event.USER_FINISH_ADD_PAK_CHANNEL, channelConfig);
      }
      setIsChannelConfigModalOpen(false);
    },
    [configSource]
  );

  // Signal to the controller that any SAT paste should go here instead of to a new channel when we have the modal open to edit a SAT channel
  useEffect(() => {
    const isUserReadyForSatUpdate =
      isChannelConfigModalOpen && configSource instanceof PineridgeChannel && U.isSatConfiguration(configSource.config);

    const payload = isUserReadyForSatUpdate ? configSource : null;

    emitEvent(Event.USER_CHANNEL_PENDING_SAT_UPDATE, payload);
  }, [isChannelConfigModalOpen, configSource]);

  function handleSetUserAgent(userAgent: string): void {
    setIsUserAgentSet(true);
    emitEvent(Event.USER_SET_USER_AGENT, userAgent);
  }

  function handleUpdateAppConfig(config: AppConfiguration): void {
    setAppConfig(config);
    emitEvent(Event.USER_UPDATE_APP_CONFIG, config);
  }

  return (
    <div className="text-gray-700">
      <div className="hidden lg:inline">
        <div className="text-lg">
          <button disabled={appConfigModalOpen} onClick={() => setAppConfigModalOpen(true)}>
            App Configuration <em>{!appConfig || !appConfig.pak ? '(PAK not set) ' : ''}</em>
            <Cog6ToothIcon className="inline h-4 w-4" />
          </button>
        </div>
        <div className="text-sm">
          User: <b>{appConfig && appConfig.userId ? appConfig.userId : 'not set'}</b> Chan:{' '}
          <b>{appConfig && appConfig.channelId ? appConfig.channelId : 'not set'}</b> Audio?{' '}
          <b>{appConfig && appConfig.allowPublishAudio ? 'true' : 'false'}</b> SAT Life:{' '}
          <b>{appConfig && appConfig.tokenLifetimeSec ? appConfig.tokenLifetimeSec : 'not set'}</b> User Agent
          {isUserAgentSet ? '' : ' (unset)'}:{' '}
          <b>{appConfig && appConfig.userAgent ? appConfig.userAgent : 'not set'}</b>
        </div>
      </div>
      <div className="lg:hidden">
        <button disabled={appConfigModalOpen} onClick={() => setAppConfigModalOpen(true)}>
          <Cog6ToothIcon className="h-12 w-12" />
        </button>
      </div>
      <AppConfigModal
        isOpen={appConfigModalOpen}
        setOpen={setAppConfigModalOpen}
        initialConfig={appConfig}
        setConfig={handleUpdateAppConfig}
        setUserAgent={handleSetUserAgent}
      />
      <ChannelConfigModal
        isOpen={isChannelConfigModalOpen}
        setOpen={setIsChannelConfigModalOpen}
        configSource={configSource}
        setConfig={handleSetChannelConfig}
      />
    </div>
  );
}
